// CSV batch importer dialog (ADR-021 §1 / requirement 1).
//
// The admin pastes or uploads a CSV whose columns are:
//   employee_code,name,birth_year,gender,height_cm,weight_kg,bp_group,site
// We POST the raw text to /v1/employees/import (admin-only) and render the
// per-row ImportResult — created/rejected counts plus each row's reason — so a
// partial import is legible. On any row created we invalidate the employees
// list so the table refreshes. The dialog never blocks on the upload outcome;
// errors are shown inline, not thrown.

import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useI18n } from "@faceheart/i18n";
import type { ImportResult } from "@faceheart/api-types";
import { employeesApi } from "../../lib/api/endpoints";
import { ApiError } from "../../lib/api/client";
import { readCsvFile } from "../../lib/csv/decode";
import { ProgressBar } from "../ui/ProgressBar";

const HEADER =
  "employee_code,name,birth_year,gender,height_cm,weight_kg,bp_group,site";

// One sample data row, reused for the textarea placeholder and the downloadable
// template so an admin sees the exact shape each column expects.
const SAMPLE_ROW = "A1234,陳大文,1990,male,170,65,normal,台北分所";

// Trigger a download of a starter CSV (header + one sample row). A UTF-8 BOM is
// prepended so Excel opens the Chinese columns (name / site) without mojibake.
function downloadTemplate() {
  const csv = `${HEADER}\n${SAMPLE_ROW}\n`;
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "employees-template.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function ImportDialog({ onClose }: { onClose: () => void }) {
  const { t } = useI18n("admin");
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (text: string) => employeesApi.import(text),
    onSuccess: (res: ImportResult) => {
      if (res.created > 0) {
        void qc.invalidateQueries({ queryKey: ["employees"] });
      }
    },
  });

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setError(null);
    // Decode bytes with UTF-8/Big5 detection (see readCsvFile): an Excel
    // "Save As CSV" on zh-TW Windows is Big5, and reading it as UTF-8 would
    // mojibake every Chinese name before it is ever uploaded.
    readCsvFile(f)
      .then((text) => setCsv(text))
      .catch(() => setError(t("employees.import.fileReadFailed")));
  }

  function submit() {
    setError(null);
    const text = csv.trim();
    if (!text) {
      setError(t("employees.import.emptyCsv"));
      return;
    }
    mut.mutate(text);
  }

  const result = mut.data;
  const apiErr =
    mut.error instanceof ApiError
      ? t("employees.import.requestFailed", {
          code: (mut.error as ApiError).code,
        })
      : mut.error
        ? t("common:errors.network")
        : null;

  return (
    <Backdrop onClose={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("employees.import.title")}
        className="w-[640px] max-w-[92vw] max-h-[88vh] overflow-y-auto rounded-lg border border-admin-border bg-admin-bg2 p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-medium">
              {t("employees.import.title")}
            </h2>
            <p className="mt-1 text-xs text-admin-text3">
              {t("employees.import.subtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-admin-text3 hover:text-admin-text1"
            aria-label={t("common:actions.close")}
          >
            ✕
          </button>
        </div>

        {/* Download the starter template — prominent full-width primary action
            so admins grab the correctly-shaped CSV before anything else. */}
        <button
          type="button"
          onClick={downloadTemplate}
          className="mb-3 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-admin-cyan px-4 py-2.5 text-sm font-medium text-admin-bg hover:bg-admin-cyan/90"
        >
          <Download className="h-4 w-4" />
          {t("employees.import.downloadTemplate")}
        </button>

        {/* Column contract reminder. */}
        <div className="mb-3 rounded border border-admin-border bg-admin-bg3/60 px-3 py-2">
          <div className="mb-1 text-xs text-admin-text3">
            {t("employees.import.columns")}
          </div>
          <div className="font-mono text-[11px] text-admin-text2 break-all">
            {HEADER}
          </div>
        </div>

        <div className="mb-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="rounded border border-admin-border px-3 py-1.5 text-sm text-admin-text2 hover:bg-admin-bg3"
          >
            {t("employees.import.chooseFile")}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={onPickFile}
          />
          <span className="text-xs text-admin-text3">
            {t("employees.import.orPaste")}
          </span>
        </div>

        <textarea
          value={csv}
          onChange={(e) => setCsv(e.target.value)}
          spellCheck={false}
          rows={8}
          placeholder={HEADER + "\n" + SAMPLE_ROW}
          className="w-full resize-y rounded border border-admin-border bg-admin-bg3 px-3 py-2 font-mono text-xs text-admin-text1 placeholder:text-admin-text3"
        />

        {(error || apiErr) && (
          <p className="mt-2 text-sm text-admin-red">{error ?? apiErr}</p>
        )}

        {result && <ResultPanel result={result} />}

        {mut.isPending && (
          <div className="mt-4">
            <ProgressBar label={t("employees.import.processing")} />
          </div>
        )}

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-admin-border px-4 py-2 text-sm text-admin-text2 hover:bg-admin-bg3"
          >
            {result ? t("common:actions.close") : t("common:actions.cancel")}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={mut.isPending}
            className="rounded bg-admin-cyan/90 px-4 py-2 text-sm font-medium text-admin-bg hover:bg-admin-cyan disabled:opacity-50"
          >
            {mut.isPending
              ? t("employees.import.importing")
              : t("employees.import.submit")}
          </button>
        </div>
      </div>
    </Backdrop>
  );
}

function ResultPanel({ result }: { result: ImportResult }) {
  const { t } = useI18n("admin");
  return (
    <div className="mt-4 rounded border border-admin-border bg-admin-bg3/40 p-3">
      <div className="flex gap-6 text-sm">
        <span className="text-admin-green">
          {t("employees.import.created", { n: result.created })}
        </span>
        {result.updated > 0 && (
          <span className="text-admin-cyan">
            {t("employees.import.updated", { n: result.updated })}
          </span>
        )}
        <span className={result.rejected > 0 ? "text-admin-red" : "text-admin-text3"}>
          {t("employees.import.rejected", { n: result.rejected })}
        </span>
      </div>
      {result.errors.length > 0 && (
        <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto text-xs text-admin-text2">
          {result.errors.map((e, i) => (
            <li key={i} className="flex gap-2">
              <span className="shrink-0 text-admin-text3">
                {t("employees.import.rowLabel", { row: e.row })}
                {e.employee_code ? ` (${e.employee_code})` : ""}
              </span>
              <span className="text-admin-red">
                {e.reason
                  ? t(`employees.import.reason.${e.reason}`, {
                      defaultValue: e.message,
                    })
                  : e.message}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Backdrop({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      {children}
    </div>
  );
}
